import {
  getScrollContainer,
  getScrollEventTarget,
  getIntersectionRoot,
  getScrollTop,
  scrollContainerMediaQuery,
  scrollTo,
} from '@theme/scroll-container';

(() => {
  if (window.DaoHomepageRuntime) {
    window.DaoHomepageRuntime.scan(document);
    return;
  }

  const instances = new WeakMap();

  class DaoHomepage {
    constructor(root) {
      this.root = root;
      this.cleanups = [];
      this.observers = new Set();
      this.timers = new Set();
      this.rafs = new Set();
      this.scrollListeners = new Set();
      this.preloadedImages = new Set();
      this.scrollEventTarget = getScrollEventTarget();
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
      this.coarsePointer = window.matchMedia('(pointer: coarse)');
      this.listenMedia(scrollContainerMediaQuery, () => this.rebindScrollListeners());
      this.init();
    }

    q(selector, context = this.root) {
      return context.querySelector(selector);
    }

    qa(selector, context = this.root) {
      return [...context.querySelectorAll(selector)];
    }

    listen(target, type, listener, options) {
      if (!target) return;
      target.addEventListener(type, listener, options);
      this.cleanups.push(() => target.removeEventListener(type, listener, options));
    }

    listenMedia(query, listener) {
      if (query.addEventListener) {
        this.listen(query, 'change', listener);
      } else {
        query.addListener(listener);
        this.cleanups.push(() => query.removeListener(listener));
      }
    }

    listenPageScroll(listener) {
      this.scrollListeners.add(listener);
      this.scrollEventTarget.addEventListener('scroll', listener, { passive: true });
      this.cleanups.push(() => {
        this.scrollEventTarget?.removeEventListener('scroll', listener);
        this.scrollListeners.delete(listener);
      });
    }

    rebindScrollListeners() {
      const nextTarget = getScrollEventTarget();
      if (nextTarget === this.scrollEventTarget) return;
      this.scrollListeners.forEach((listener) => {
        this.scrollEventTarget?.removeEventListener('scroll', listener);
        nextTarget.addEventListener('scroll', listener, { passive: true });
      });
      this.scrollEventTarget = nextTarget;
      this.scrollListeners.forEach((listener) => listener());
    }

    getViewportHeight() {
      return getScrollContainer().clientHeight || window.innerHeight;
    }

    getElementScrollTop(element) {
      const container = getScrollContainer();
      const isDocumentRoot = container === document.documentElement || container === document.body;
      const containerTop = isDocumentRoot ? 0 : container.getBoundingClientRect().top;
      return getScrollTop() + element.getBoundingClientRect().top - containerTop;
    }

    observe(callback, options) {
      const observer = new IntersectionObserver(callback, options);
      this.observers.add(observer);
      return observer;
    }

    timeout(callback, delay) {
      const id = window.setTimeout(() => {
        this.timers.delete(id);
        callback();
      }, delay);
      this.timers.add(id);
      return id;
    }

    clearTimer(id) {
      if (!id) return;
      window.clearTimeout(id);
      this.timers.delete(id);
    }

    frame(callback) {
      const id = window.requestAnimationFrame((time) => {
        this.rafs.delete(id);
        callback(time);
      });
      this.rafs.add(id);
      return id;
    }

    cancelFrame(id) {
      if (!id) return;
      window.cancelAnimationFrame(id);
      this.rafs.delete(id);
    }

    preloadImage(url) {
      if (!url || this.preloadedImages.has(url)) return;
      this.preloadedImages.add(url);
      const image = new Image();
      image.decoding = 'async';
      image.src = url;
    }

    init() {
      this.root.classList.add('is-initialized');
      this.initProgressAndReveals();
      this.initPointerDetails();
      this.initStory();
      this.initRitual();
      this.initNotes();
      this.initSectionMotion();
      this.initHero();
      this.initFinalCta();
      this.initBridge();
      this.initObjects();
    }

    initProgressAndReveals() {
      const progress = this.q('[data-progress]');
      let progressRaf = 0;
      const updateProgress = () => {
        progressRaf = 0;
        const scrollContainer = getScrollContainer();
        const max = scrollContainer.scrollHeight - scrollContainer.clientHeight;
        if (progress) progress.style.height = `${(max > 0 ? Math.min(1, getScrollTop() / max) : 0) * 100}%`;
      };
      const onScroll = () => {
        if (!progressRaf) progressRaf = this.frame(updateProgress);
      };
      this.listenPageScroll(onScroll);
      updateProgress();

      const observer = this.observe((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('in');
          observer.unobserve(entry.target);
        });
      }, { threshold: 0.12 });
      this.qa('.reveal-card,.reveal-mask').forEach((element) => observer.observe(element));
    }

    initPointerDetails() {
      if (!this.finePointer.matches || this.reducedMotion.matches) return;

      this.qa('.magnetic').forEach((element) => {
        this.listen(element, 'pointermove', (event) => {
          const rect = element.getBoundingClientRect();
          const x = (event.clientX - rect.left - rect.width / 2) * 0.08;
          const y = (event.clientY - rect.top - rect.height / 2) * 0.12;
          element.style.transform = `translate(${x}px,${y}px)`;
        });
        this.listen(element, 'pointerleave', () => { element.style.transform = ''; });
      });

      this.qa('.tilt-card').forEach((card) => {
        this.listen(card, 'pointermove', (event) => {
          const rect = card.getBoundingClientRect();
          const x = (event.clientX - rect.left) / rect.width;
          const y = (event.clientY - rect.top) / rect.height;
          card.style.transform = `translateY(-6px) rotateX(${(0.5 - y) * 3.2}deg) rotateY(${(x - 0.5) * 4.2}deg)`;
        });
        this.listen(card, 'pointerleave', () => { card.style.transform = ''; });
      });
    }

    initStory() {
      const nav = this.qa('[data-story-nav]');
      const image = this.q('[data-story-image]');
      const frame = this.q('[data-story-text-frame]');
      if (!nav.length || !image) return;

      const copy = [
        ['01 / THE STRIKE', 'Lightning leaves a permanent mark.', 'Heat and force can split the grain, darken the wood and expose the structure beneath.'],
        ['02 / SURVIVAL', 'The tree remains alive.', 'The scar is visible, but growth continues around it.'],
        ['03 / HEALING', 'The tree grows around the wound.', 'New growth forms beside the old damage, preserving a visible record of the strike.'],
        ['04 / SELECTION', 'Only sound sections are selected.', 'Each piece is examined for structure, grain and visible character before shaping.'],
        ['05 / THE FORM', 'Traces of the strike remain.', 'The finished object retains its own grain, tone, cracks and natural variation.'],
      ];
      const items = nav.map((button, index) => ({
        step: copy[index][0],
        title: copy[index][1],
        body: copy[index][2],
        image: button.dataset.src,
        mobileFocus: button.dataset.mobileFocus,
      }));
      let active = 0;
      let storyNear = false;
      let imageTimer = 0;
      let copyTimer = 0;
      const setStory = (index) => {
        const next = Math.max(0, Math.min(items.length - 1, index));
        const item = items[next];
        active = next;
        image.style.setProperty('--story-mobile-focus', item.mobileFocus);
        const shutter = this.q('[data-story-shutter]');
        shutter?.classList.remove('run');
        if (shutter) void shutter.offsetWidth;
        shutter?.classList.add('run');
        frame?.classList.add('changing');
        this.clearTimer(imageTimer);
        this.clearTimer(copyTimer);
        if (image.src !== item.image) {
          image.style.opacity = '0';
          image.style.transform = 'scale(1.07)';
          imageTimer = this.timeout(() => {
            image.src = item.image;
            image.style.opacity = '1';
            image.style.transform = 'scale(1.015)';
          }, 170);
        }
        copyTimer = this.timeout(() => {
          const step = this.q('[data-story-step]');
          const title = this.q('[data-story-title]');
          const body = this.q('[data-story-body]');
          if (step) step.textContent = item.step;
          if (title) title.textContent = item.title;
          if (body) body.textContent = item.body;
          frame?.classList.remove('changing');
        }, 190);
        if (storyNear) {
          this.preloadImage(items[Math.min(items.length - 1, next + 1)]?.image);
          this.preloadImage(items[Math.max(0, next - 1)]?.image);
        }
        nav.forEach((button, buttonIndex) => button.classList.toggle('active', buttonIndex === next));
      };
      nav.forEach((button, index) => this.listen(button, 'click', () => setStory(index)));
      const triggers = this.qa('[data-story-trigger]');
      if (triggers.length) {
        const observer = this.observe((entries) => entries.forEach((entry) => {
          if (entry.isIntersecting) setStory(Number(entry.target.dataset.storyTrigger));
        }), { rootMargin: '-38% 0px -38% 0px', threshold: 0 });
        triggers.forEach((trigger) => observer.observe(trigger));
      }
      const storySection = image.closest('[data-story]');
      if (storySection) {
        const warmObserver = this.observe(([entry]) => {
          if (!entry?.isIntersecting) return;
          storyNear = true;
          this.preloadImage(items[active]?.image);
          this.preloadImage(items[Math.min(items.length - 1, active + 1)]?.image);
          warmObserver.unobserve(storySection);
        }, { root: getIntersectionRoot(), rootMargin: '480px 0px', threshold: 0 });
        warmObserver.observe(storySection);
      }
      setStory(active);
    }

    initRitual() {
      const section = this.q('[data-ritual]');
      const nav = this.qa('[data-ritual-nav]', section);
      if (!section || !nav.length) return;
      const canvas = this.q('[data-ritual-particles]', section);
      const panels = this.qa('[data-ritual-panel]', section);
      const artifactNames = ['BRONZE INCENSE BURNER', 'JIAO BEI', 'ZHEN WU LING QIAN', 'ZHEN WU DA DI'];
      const lastArtifactIndex = artifactNames.length - 1;
      const dial = this.q('[data-ritual-dial]', section);
      let active = 0;
      let particleScene = null;
      let particleLoadStarted = false;
      let particleNear = false;
      let particleProgress = 0;
      let particleProgressRaf = 0;
      let particleDisposed = false;

      const setParticleState = (state, detail = {}) => {
        if (particleDisposed) return;
        if (state === 'artifact') {
          const index = Math.max(0, Math.min(artifactNames.length - 1, Number(detail.index) || 0));
          const count = this.q('[data-ritual-artifact-count]', section);
          const name = this.q('[data-ritual-artifact-name]', section);
          if (count) count.textContent = `${String(index + 1).padStart(2, '0')} / 04`;
          if (name) name.textContent = artifactNames[index];
          return;
        }
        if (state === 'rotation') {
          dial?.style.setProperty('--ritual-rotation', `${Number(detail.rotation) || 0}rad`);
          return;
        }
        section.dataset.ritualParticleState = state;
        section.dataset.ritualParticleTier = detail.tier || 'fallback';
        if (detail.reason) {
          section.dataset.ritualParticleError = String(detail.reason).slice(0, 120);
        } else {
          delete section.dataset.ritualParticleError;
        }
        section.classList.toggle('ritual-particles-ready', state === 'ready');
        section.classList.toggle('ritual-particles-loading', state === 'loading');
      };

      const selectParticleTier = () => {
        const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
        const memory = Number(navigator.deviceMemory) || 0;
        const cores = Number(navigator.hardwareConcurrency) || 0;
        if (this.reducedMotion.matches || connection?.saveData || (memory && memory <= 2) || (cores && cores <= 2)) return 'fallback';
        if (window.innerWidth <= 900 || this.coarsePointer.matches || (memory && memory <= 4) || (cores && cores <= 4)) return 'medium';
        return 'high';
      };

      const updateParticleProgress = () => {
        particleProgressRaf = 0;
        const viewport = this.getViewportHeight();
        const range = Math.max(1, section.offsetHeight - viewport);
        particleProgress = Math.max(0, Math.min(1, (getScrollTop() - this.getElementScrollTop(section)) / range));
        const journey = particleProgress * (artifactNames.length - 1);
        const next = Math.max(0, Math.min(artifactNames.length - 1, Math.round(journey)));
        const distance = Math.abs(journey - next);
        const normalizedDistance = Math.max(0, Math.min(1, (distance - 0.24) / 0.22));
        const copyVisibility = 1 - normalizedDistance * normalizedDistance * (3 - 2 * normalizedDistance);
        section.style.setProperty('--ritual-copy-visibility', copyVisibility.toFixed(3));
        setRitual(next);
        particleScene?.setProgress(particleProgress);
      };
      const scheduleParticleProgress = () => {
        if (!particleProgressRaf) particleProgressRaf = this.frame(updateParticleProgress);
      };

      const loadParticles = async () => {
        if (!canvas || particleLoadStarted || particleDisposed) return;
        const tier = selectParticleTier();
        if (tier === 'fallback') {
          setParticleState('fallback', { tier });
          return;
        }
        const moduleUrl = section.dataset.ritualParticleModule;
        const assetUrl = tier === 'high' ? section.dataset.ritualParticleHigh : section.dataset.ritualParticleMedium;
        if (!moduleUrl || !assetUrl) {
          setParticleState('fallback', { tier: 'fallback' });
          return;
        }
        particleLoadStarted = true;
        setParticleState('loading', { tier });
        try {
          const { createRitualParticleScene } = await import(moduleUrl);
          if (particleDisposed || this.reducedMotion.matches) return;
          particleScene = createRitualParticleScene({ section, canvas, assetUrl, tier, onState: setParticleState });
          particleScene.setProgress(particleProgress);
        } catch (error) {
          particleLoadStarted = false;
          setParticleState('fallback', {
            tier: 'fallback',
            reason: error instanceof Error ? error.message : String(error),
          });
        }
      };

      const setRitual = (index) => {
        const next = Math.max(0, Math.min(artifactNames.length - 1, index));
        if (next === active && section.dataset.ritualState === String(next)) return;
        active = next;
        nav.forEach((button, buttonIndex) => button.classList.toggle('active', buttonIndex === next));
        panels.forEach((panel, panelIndex) => {
          const isActive = panelIndex === next;
          panel.classList.toggle('active', isActive);
          panel.setAttribute('aria-hidden', String(!isActive));
        });
        section.dataset.ritualState = String(next);
      };

      const scrollRitualTo = (index) => {
        const targetIndex = Math.max(0, Math.min(lastArtifactIndex, index));
        const viewport = this.getViewportHeight();
        const range = Math.max(1, section.offsetHeight - viewport);
        const top = this.getElementScrollTop(section);
        scrollTo({
          top: top + range * (targetIndex / lastArtifactIndex),
          behavior: this.reducedMotion.matches ? 'auto' : 'smooth',
        });
      };

      nav.forEach((button, index) => this.listen(button, 'click', () => scrollRitualTo(index)));

      if (canvas) {
        const loadObserver = this.observe(([entry]) => {
          particleNear = Boolean(entry?.isIntersecting);
          if (particleNear) loadParticles();
        }, { root: getIntersectionRoot(), rootMargin: '560px 0px', threshold: 0 });
        loadObserver.observe(section);
        this.listenPageScroll(scheduleParticleProgress);
        this.listen(window, 'resize', scheduleParticleProgress, { passive: true });
        this.listenMedia(this.reducedMotion, () => {
          if (this.reducedMotion.matches) {
            particleScene?.dispose();
            particleScene = null;
            setParticleState('fallback', { tier: 'fallback' });
            return;
          }
          particleLoadStarted = false;
          if (particleNear) loadParticles();
        });
        updateParticleProgress();
        this.cleanups.push(() => {
          particleDisposed = true;
          this.cancelFrame(particleProgressRaf);
          section.style.removeProperty('--ritual-copy-visibility');
          particleScene?.dispose();
          particleScene = null;
        });
      }
      setRitual(active);
    }

    initNotes() {
      const buttons = this.qa('[data-note-jump]');
      const rail = this.q('.notes-index-rail');
      const imageStage = this.q('[data-note-image]');
      const imageMedia = this.q('[data-note-media]');
      const imageLink = this.q('[data-note-image-link]');
      const copy = this.q('[data-note-copy]');
      if (!buttons.length || !imageStage || !imageMedia || !copy) return;
      const notesSection = imageStage.closest('.notes');
      const items = buttons.map((button) => ({
        date: button.dataset.date,
        title: button.dataset.title,
        excerpt: button.dataset.excerpt,
        alt: button.dataset.alt,
        mobileFocus: button.dataset.mobileFocus || '50% 50%',
        bg: button.dataset.bg,
        url: button.dataset.url,
      }));
      let active = 0;
      let notesNear = false;
      let busy = false;
      let changeTimer = 0;
      const setNote = (index, immediate = false) => {
        if (busy && !immediate) return;
        busy = true;
        active = (index + items.length) % items.length;
        const item = items[active];
        imageStage.classList.add('changing');
        copy.classList.add('changing');
        this.clearTimer(changeTimer);
        const commit = () => {
          const count = String(active + 1).padStart(2, '0');
          const indexElement = this.q('[data-note-index]');
          const date = this.q('[data-note-date]');
          const title = this.q('[data-note-title]');
          const excerpt = this.q('[data-note-excerpt]');
          const link = this.q('[data-note-link]');
          if (indexElement) indexElement.textContent = count;
          if (date) date.textContent = item.date;
          if (title) title.textContent = item.title;
          if (excerpt) excerpt.textContent = item.excerpt;
          if (link) link.href = item.url;
          if (imageLink) {
            imageLink.href = item.url;
            imageLink.setAttribute('aria-label', `Open ${item.title}`);
          }
          imageStage.style.setProperty('--note-image', notesNear && item.bg ? `url("${item.bg.replace(/"/g, '\\"')}")` : 'none');
          imageStage.style.setProperty('--note-focus', item.mobileFocus);
          imageMedia.hidden = !item.bg;
          if (item.bg && imageMedia.src !== item.bg) imageMedia.src = item.bg;
          imageMedia.alt = item.alt || item.title;
          imageMedia.style.objectPosition = item.mobileFocus;
          buttons.forEach((button, buttonIndex) => {
            const isActive = buttonIndex === active;
            button.classList.toggle('active', isActive);
            button.setAttribute('aria-pressed', String(isActive));
          });
          const activeButton = buttons[active];
          if (rail && activeButton && rail.scrollWidth > rail.clientWidth) {
            const railRect = rail.getBoundingClientRect();
            const buttonRect = activeButton.getBoundingClientRect();
            const targetLeft = rail.scrollLeft
              + buttonRect.left
              - railRect.left
              - (rail.clientWidth - activeButton.offsetWidth) / 2;
            rail.scrollTo({
              left: Math.max(0, targetLeft),
              behavior: immediate || this.reducedMotion.matches ? 'auto' : 'smooth',
            });
          }
          imageStage.classList.remove('changing');
          copy.classList.remove('changing');
          if (notesNear) {
            this.preloadImage(items[(active + 1) % items.length]?.bg);
            this.preloadImage(items[(active - 1 + items.length) % items.length]?.bg);
          }
          busy = false;
        };
        if (immediate) commit();
        else changeTimer = this.timeout(commit, 260);
      };
      buttons.forEach((button, index) => this.listen(button, 'click', () => setNote(index)));
      this.listen(this.q('[data-note-prev]'), 'click', () => setNote(active - 1));
      this.listen(this.q('[data-note-next]'), 'click', () => setNote(active + 1));
      setNote(0, true);
      if (notesSection) {
        const warmObserver = this.observe(([entry]) => {
          if (!entry?.isIntersecting) return;
          notesNear = true;
          const current = items[active];
          imageStage.style.setProperty('--note-image', current?.bg ? `url("${current.bg.replace(/"/g, '\\"')}")` : 'none');
          this.preloadImage(items[(active + 1) % items.length]?.bg);
          warmObserver.unobserve(notesSection);
        }, { root: getIntersectionRoot(), rootMargin: '560px 0px', threshold: 0 });
        warmObserver.observe(notesSection);
      }
    }

    initSectionMotion() {
      const sections = this.qa('[data-ui-section]');
      const observer = this.observe((entries) => entries.forEach((entry) => {
        if (entry.isIntersecting) entry.target.classList.add('ui-in');
      }), { threshold: 0.08, rootMargin: '0px 0px -8% 0px' });
      sections.forEach((section) => observer.observe(section));

      if (this.reducedMotion.matches) return;
      const media = this.qa('.bridge-object-media img');
      let driftRaf = 0;
      const update = () => {
        driftRaf = 0;
        media.forEach((element) => {
          const rect = element.getBoundingClientRect();
          const progress = (window.innerHeight - rect.top) / (window.innerHeight + rect.height);
          const y = (Math.max(0, Math.min(1, progress)) - 0.5) * 12;
          element.style.translate = `0 ${y * 0.35}px`;
        });
      };
      const onScroll = () => { if (!driftRaf) driftRaf = this.frame(update); };
      this.listenPageScroll(onScroll);
      update();
    }

    initHero() {
      const hero = this.q('[data-hero-exact]');
      if (!hero) return;
      const copy = this.q('[data-hero-exact-copy]', hero);
      const baseImage = this.q('.hero-exact-base', hero);
      const revealImage = this.q('.hero-exact-reveal[data-src]', hero);
      const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
      let moveRaf = 0;
      let ambientRaf = 0;
      let visible = false;
      let mobileMode = false;
      let touching = false;
      let manualUntil = 0;
      let cycleStartedAt = performance.now();
      const loadRevealImage = () => {
        if (!revealImage?.dataset.src || revealImage.src) return;
        revealImage.src = revealImage.dataset.src;
      };
      if (baseImage?.complete) loadRevealImage();
      else {
        this.listen(baseImage, 'load', loadRevealImage, { once: true });
        this.listen(baseImage, 'error', loadRevealImage, { once: true });
      }
      const setReveal = (x, y, radius, opacity = 1, haze = 0.85) => {
        hero.style.setProperty('--hero-mx', `${clamp(x, 0, 100).toFixed(2)}%`);
        hero.style.setProperty('--hero-my', `${clamp(y, 0, 100).toFixed(2)}%`);
        hero.style.setProperty('--hero-radius', `${Math.max(40, radius).toFixed(1)}px`);
        hero.style.setProperty('--hero-reveal-opacity', clamp(opacity, 0, 1).toFixed(3));
        hero.style.setProperty('--hero-haze-opacity', clamp(haze, 0, 1).toFixed(3));
      };
      const isMobile = () => window.innerWidth <= 820 || this.coarsePointer.matches || !this.finePointer.matches;
      const revealAt = (clientX, clientY, radius) => {
        const rect = hero.getBoundingClientRect();
        if (!rect.width || !rect.height) return;
        setReveal((clamp(clientX - rect.left, 0, rect.width) / rect.width) * 100, (clamp(clientY - rect.top, 0, rect.height) / rect.height) * 100, radius, 1, 0.92);
      };
      const stopAmbient = () => {
        this.cancelFrame(ambientRaf);
        ambientRaf = 0;
      };
      const ambient = (now) => {
        ambientRaf = 0;
        if (!visible || !mobileMode || this.reducedMotion.matches || !hero.isConnected) return;
        if (!touching && now >= manualUntil) {
          const elapsed = (now - cycleStartedAt) / 1000;
          const bloom = (Math.sin(elapsed * 0.72 - Math.PI / 2) + 1) / 2;
          const secondary = (Math.sin(elapsed * 0.23 + 0.8) + 1) / 2;
          setReveal(66 + Math.sin(elapsed * 0.34) * 13 + Math.sin(elapsed * 0.11) * 4, 48 + Math.cos(elapsed * 0.29) * 7, 78 + bloom * 350 + secondary * 22, 0.58 + bloom * 0.42, 0.24 + bloom * 0.64);
        }
        ambientRaf = this.frame(ambient);
      };
      const startAmbient = () => {
        if (ambientRaf || !visible || !mobileMode || this.reducedMotion.matches) return;
        cycleStartedAt = performance.now();
        ambientRaf = this.frame(ambient);
      };
      const syncMode = () => {
        mobileMode = isMobile();
        if (this.reducedMotion.matches) {
          stopAmbient();
          if (mobileMode) setReveal(67, 48, Math.min(350, Math.max(260, window.innerWidth * 0.82)), 0.94, 0.72);
        } else if (mobileMode) startAmbient();
        else {
          stopAmbient();
          hero.style.setProperty('--hero-radius', '230px');
          hero.style.setProperty('--hero-reveal-opacity', '1');
          hero.style.setProperty('--hero-haze-opacity', '.85');
        }
      };
      this.listen(hero, 'pointerdown', (event) => {
        if (!mobileMode) return;
        touching = true;
        manualUntil = performance.now() + 900;
        hero.classList.add('is-touching', 'has-hero-interacted');
        revealAt(event.clientX, event.clientY, clamp(Math.min(window.innerWidth, window.innerHeight) * 0.78, 250, 390));
        try { hero.setPointerCapture(event.pointerId); } catch (_) { /* Pointer capture is optional. */ }
      }, { passive: true });
      this.listen(hero, 'pointermove', (event) => {
        this.cancelFrame(moveRaf);
        moveRaf = this.frame(() => {
          if (mobileMode) {
            if (touching) revealAt(event.clientX, event.clientY, clamp(Math.min(window.innerWidth, window.innerHeight) * 0.78, 250, 390));
            return;
          }
          const rect = hero.getBoundingClientRect();
          hero.style.setProperty('--hero-mx', `${((clamp(event.clientX - rect.left, 0, rect.width) / rect.width) * 100).toFixed(2)}%`);
          hero.style.setProperty('--hero-my', `${((clamp(event.clientY - rect.top, 0, rect.height) / rect.height) * 100).toFixed(2)}%`);
        });
      }, { passive: true });
      const endTouch = (event) => {
        if (!touching) return;
        touching = false;
        manualUntil = performance.now() + 1100;
        hero.classList.remove('is-touching');
        try { hero.releasePointerCapture(event.pointerId); } catch (_) { /* Pointer capture is optional. */ }
      };
      this.listen(hero, 'pointerup', endTouch, { passive: true });
      this.listen(hero, 'pointercancel', endTouch, { passive: true });
      this.listen(hero, 'pointerleave', (event) => { if (mobileMode && touching) endTouch(event); }, { passive: true });
      const observer = this.observe(([entry]) => {
        visible = Boolean(entry?.isIntersecting);
        if (visible) startAmbient(); else stopAmbient();
      }, { threshold: 0.01 });
      observer.observe(hero);
      this.timeout(() => hero.classList.add('hero-hint-settled'), 6200);
      this.listen(window, 'resize', syncMode, { passive: true });
      this.listenMedia(this.finePointer, syncMode);
      this.listenMedia(this.coarsePointer, syncMode);
      this.listenMedia(this.reducedMotion, syncMode);
      let copyRaf = 0;
      this.listenPageScroll(() => {
        if (copyRaf || !copy) return;
        copyRaf = this.frame(() => {
          copyRaf = 0;
          const progress = clamp(-hero.getBoundingClientRect().top / (window.innerHeight * 0.78), 0, 1);
          copy.style.opacity = String(1 - progress * 0.9);
          copy.style.transform = `translateY(${-progress * 24}px)`;
        });
      });
      syncMode();
    }

    initFinalCta() {
      const final = this.q('[data-final-cta]');
      if (!final) return;
      const observer = this.observe((entries) => entries.forEach((entry) => final.classList.toggle('is-visible', entry.isIntersecting)), { threshold: 0.28 });
      observer.observe(final);
      final.style.setProperty('--fx', '0');
      final.style.setProperty('--fy', '0');
      if (this.finePointer.matches && !this.reducedMotion.matches) {
        this.listen(final, 'pointermove', (event) => {
          const rect = final.getBoundingClientRect();
          final.style.setProperty('--fx', ((((event.clientX - rect.left) / rect.width) - 0.5) * 2).toFixed(3));
          final.style.setProperty('--fy', ((((event.clientY - rect.top) / rect.height) - 0.5) * 2).toFixed(3));
        });
        this.listen(final, 'pointerleave', () => {
          final.style.setProperty('--fx', '0');
          final.style.setProperty('--fy', '0');
        });
      }
      this.qa('[data-final-action]', final).forEach((link) => {
        const className = link.dataset.finalAction === 'collection' ? 'hover-collection' : 'hover-notes';
        this.listen(link, 'pointerenter', () => final.classList.add(className));
        this.listen(link, 'pointerleave', () => final.classList.remove(className));
      });
    }

    initBridge() {
      const bridge = this.q('[data-bridge-object-stage]');
      const media = bridge?.querySelector('.bridge-object-media');
      if (!bridge || !media || !this.finePointer.matches || this.reducedMotion.matches) return;
      this.listen(bridge, 'pointermove', (event) => {
        const rect = bridge.getBoundingClientRect();
        media.style.setProperty('--bridge-x', `${(((event.clientX - rect.left) / rect.width) * 100).toFixed(2)}%`);
        media.style.setProperty('--bridge-y', `${(((event.clientY - rect.top) / rect.height) * 100).toFixed(2)}%`);
      }, { passive: true });
      this.listen(bridge, 'pointerleave', () => {
        media.style.setProperty('--bridge-x', '55%');
        media.style.setProperty('--bridge-y', '46%');
      });
    }

    initObjects() {
      const section = this.q('[data-lux-objects]');
      if (!section) return;
      const showcase = this.q('[data-lux-showcase]', section);
      const camera = this.q('[data-lux-camera]', section);
      const caustic = this.q('[data-lux-caustic]', section);
      const veil = this.q('[data-lux-veil]', section);
      const copy = this.q('[data-lux-copy]', section);
      const kicker = this.q('[data-lux-kicker]', section);
      const title = this.q('[data-lux-title]', section);
      const description = this.q('[data-lux-desc]', section);
      const link = this.q('[data-lux-link]', section);
      const indexElement = this.q('[data-lux-index]', section);
      const tabs = this.qa('[data-lux-jump]', section);
      const particleHost = this.q('[data-lux-particles]', section);
      const layers = {
        a: { element: this.q('[data-lux-layer="a"]', section), image: this.q('[data-lux-img="a"]', section), sheen: this.q('[data-lux-sheen="a"]', section), reflection: this.q('[data-lux-reflect="a"]', section), index: -1 },
        b: { element: this.q('[data-lux-layer="b"]', section), image: this.q('[data-lux-img="b"]', section), sheen: this.q('[data-lux-sheen="b"]', section), reflection: this.q('[data-lux-reflect="b"]', section), index: -1 },
      };
      if (!showcase || !camera || !caustic || !veil || !copy || !tabs.length || !layers.a.element || !layers.b.element) return;
      const items = tabs.map((tab) => ({ src: tab.dataset.src, url: tab.dataset.url, kicker: tab.dataset.kicker, title: tab.dataset.title, description: tab.dataset.desc, align: tab.dataset.align, fit: Number(tab.dataset.fit) }));
      const particles = [];
      if (particleHost) {
        const fragment = document.createDocumentFragment();
        for (let index = 0; index < 12; index += 1) {
          const particle = document.createElement('i');
          particle.className = 'lux-particle';
          particle.style.setProperty('--x', `${34 + Math.random() * 32}%`);
          particle.style.setProperty('--y', `${31 + Math.random() * 36}%`);
          particle.style.setProperty('--s', `${(0.8 + Math.random() * 1.5).toFixed(2)}px`);
          particle.style.setProperty('--o', `${(0.09 + Math.random() * 0.2).toFixed(2)}`);
          particle.style.setProperty('--d', `${(6 + Math.random() * 6).toFixed(2)}s`);
          particle.style.setProperty('--delay', `${(-Math.random() * 8).toFixed(2)}s`);
          fragment.appendChild(particle);
          particles.push(particle);
        }
        particleHost.appendChild(fragment);
      }
      this.cleanups.push(() => particles.forEach((particle) => particle.remove()));
      const clamp = (value, min = 0, max = 1) => Math.max(min, Math.min(max, value));
      const ease = (value) => value < 0.5 ? 4 * value ** 3 : 1 - ((-2 * value + 2) ** 3) / 2;
      const easeOut = (value) => 1 - (1 - value) ** 3;
      let glintTimer = 0;
      let renderRaf = 0;
      const fillLayer = (layer, index) => {
        if (index < 0 || index >= items.length) { layer.index = -1; layer.element.style.opacity = '0'; return; }
        if (layer.index === index) return;
        const item = items[index];
        layer.index = index;
        layer.image.src = item.src;
        layer.image.alt = item.title;
        layer.sheen.src = item.src;
        layer.reflection.src = item.src;
        layer.element.style.setProperty('--fit', item.fit);
      };
      const sheen = (layer) => {
        if (this.reducedMotion.matches || !layer?.sheen?.animate) return;
        layer.sheen.getAnimations().forEach((animation) => animation.cancel());
        layer.sheen.animate([
          { opacity: 0, maskPosition: '140% 0', webkitMaskPosition: '140% 0' },
          { offset: 0.18, opacity: 0.08 },
          { offset: 0.5, opacity: 0.4, maskPosition: '50% 0', webkitMaskPosition: '50% 0' },
          { offset: 0.78, opacity: 0.14 },
          { opacity: 0, maskPosition: '-34% 0', webkitMaskPosition: '-34% 0' },
        ], { duration: 980, delay: 90, easing: 'cubic-bezier(.2,.72,.2,1)' });
      };
      const runGlint = () => {
        if (this.reducedMotion.matches) return;
        this.clearTimer(glintTimer);
        showcase.classList.remove('is-glinting');
        this.frame(() => this.frame(() => showcase.classList.add('is-glinting')));
        glintTimer = this.timeout(() => showcase.classList.remove('is-glinting'), 1250);
      };
      const setCopy = (item, index, opacity) => {
        if (Number(copy.dataset.index) !== index) {
          copy.dataset.index = String(index);
          if (kicker) kicker.textContent = item.kicker;
          if (title) title.textContent = item.title;
          if (description) description.textContent = item.description;
          if (link) link.href = item.url;
          if (window.innerWidth > 980 && item.align === 'right') {
            copy.style.left = 'auto'; copy.style.right = 'clamp(24px,7vw,108px)'; copy.style.textAlign = 'right';
          } else if (window.innerWidth > 980) {
            copy.style.right = 'auto'; copy.style.left = 'clamp(24px,7vw,108px)'; copy.style.textAlign = 'left';
          } else {
            copy.style.removeProperty('left'); copy.style.removeProperty('right'); copy.style.removeProperty('text-align');
          }
          if (indexElement) indexElement.textContent = String(index + 1).padStart(2, '0');
          tabs.forEach((tab, tabIndex) => tab.classList.toggle('active', tabIndex === index));
          sheen(index % 2 === 0 ? layers.a : layers.b);
          this.timeout(runGlint, 90);
        }
        copy.style.opacity = opacity.toFixed(3);
      };
      const styleLayer = (layer, opacity, scale, y, rotation) => {
        layer.element.style.opacity = opacity.toFixed(3);
        layer.element.style.transform = `translate(-50%,-50%) translate3d(0,${y.toFixed(2)}px,0) scale(${scale.toFixed(4)}) rotate(${rotation.toFixed(2)}deg)`;
      };
      let mediaReady = false;
      const render = () => {
        renderRaf = 0;
        if (!mediaReady) return;
        const rect = section.getBoundingClientRect();
        const range = section.offsetHeight - this.getViewportHeight();
        const progress = range > 0 ? clamp(-rect.top / range) : 0;
        const position = progress * (items.length - 1);
        const index = Math.min(items.length - 1, Math.floor(position));
        const local = index === items.length - 1 ? 0 : position - index;
        const outgoing = index % 2 === 0 ? layers.a : layers.b;
        const incoming = index % 2 === 0 ? layers.b : layers.a;
        fillLayer(outgoing, index);
        fillLayer(incoming, index + 1);
        const outgoingTime = clamp((local - 0.68) / 0.13);
        const incomingTime = clamp((local - 0.78) / 0.2);
        const outgoingEase = ease(outgoingTime);
        const incomingEase = easeOut(incomingTime);
        styleLayer(outgoing, index === items.length - 1 ? 1 : 1 - outgoingEase, 1 + outgoingEase * 0.03, -outgoingEase * 9, outgoingEase * 0.9);
        styleLayer(incoming, index === items.length - 1 ? 0 : incomingEase, 0.962 + incomingEase * 0.038, (1 - incomingEase) * 10, (1 - incomingEase) * -0.8);
        const energy = Math.max(Math.sin(outgoingTime * Math.PI), Math.sin(incomingTime * Math.PI)) * 0.92;
        camera.style.transform = `translate3d(0,${(-outgoingEase * 2 + incomingEase * 1.5).toFixed(2)}px,0) scale(${(1 + energy * 0.009).toFixed(4)})`;
        caustic.style.opacity = (0.28 + energy * 0.3).toFixed(3);
        caustic.style.transform = `translate(-50%,-50%) scale(${(0.94 + energy * 0.055).toFixed(3)})`;
        veil.style.opacity = (energy * 0.68).toFixed(3);
        veil.style.transform = `translate(-50%,-50%) scale(${(0.9 + energy * 0.075).toFixed(3)}) rotate(${(-7 + energy * 5).toFixed(2)}deg)`;
        let copyIndex = index;
        let copyOpacity = 1;
        if (index < items.length - 1) {
          if (local < 0.68) { copyIndex = index; copyOpacity = 1; }
          else if (local < 0.79) { copyIndex = index; copyOpacity = 1 - ease((local - 0.68) / 0.11); }
          else if (local < 0.82) { copyIndex = index + 1; copyOpacity = 0; }
          else { copyIndex = index + 1; copyOpacity = easeOut((local - 0.82) / 0.14); }
        }
        setCopy(items[copyIndex], copyIndex, copyOpacity);
      };
      const scheduleRender = () => { if (!renderRaf) renderRaf = this.frame(render); };
      tabs.forEach((tab, index) => this.listen(tab, 'click', () => {
        const range = section.offsetHeight - this.getViewportHeight();
        const top = this.getElementScrollTop(section);
        scrollTo({ top: top + range * (index / (items.length - 1)), behavior: this.reducedMotion.matches ? 'auto' : 'smooth' });
      }));
      this.listenPageScroll(scheduleRender);
      this.listen(window, 'resize', scheduleRender, { passive: true });
      const warmObserver = this.observe(([entry]) => {
        if (!entry?.isIntersecting || mediaReady) return;
        mediaReady = true;
        fillLayer(layers.a, 0);
        fillLayer(layers.b, 1);
        setCopy(items[0], 0, 1);
        render();
        warmObserver.unobserve(section);
      }, { root: getIntersectionRoot(), rootMargin: '560px 0px', threshold: 0 });
      warmObserver.observe(section);
    }

    destroy() {
      this.cleanups.splice(0).reverse().forEach((cleanup) => cleanup());
      this.observers.forEach((observer) => observer.disconnect());
      this.timers.forEach((timer) => window.clearTimeout(timer));
      this.rafs.forEach((frame) => window.cancelAnimationFrame(frame));
      this.observers.clear();
      this.timers.clear();
      this.rafs.clear();
      this.preloadedImages.clear();
      this.qa('*').forEach((element) => element.getAnimations?.().forEach((animation) => animation.cancel()));
      this.root.classList.remove('is-initialized');
    }
  }

  const init = (root) => {
    if (!(root instanceof HTMLElement) || instances.has(root)) return;
    instances.set(root, new DaoHomepage(root));
  };

  const scan = (context = document) => {
    if (context instanceof HTMLElement && context.matches('[data-dao-homepage]')) init(context);
    context.querySelectorAll?.('[data-dao-homepage]').forEach(init);
  };

  const destroyIn = (context) => {
    const roots = [];
    if (context instanceof HTMLElement && context.matches('[data-dao-homepage]')) roots.push(context);
    context.querySelectorAll?.('[data-dao-homepage]').forEach((root) => roots.push(root));
    roots.forEach((root) => {
      instances.get(root)?.destroy();
      instances.delete(root);
    });
  };

  window.DaoHomepageRuntime = { scan, destroyIn };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => scan(document), { once: true });
  else scan(document);
  document.addEventListener('shopify:section:load', (event) => scan(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroyIn(event.target));
  document.addEventListener('shopify:section:select', (event) => scan(event.target));
})();
